import HackleDevTools from '@hackler/javascript-devtools';
import {
  HackleProvider as HackleProvider_,
  HackleSubscriptionOperationsBuilder,
  HackleSubscriptionStatus,
  createInstance,
} from '@hackler/react-sdk';
import Cookies from 'js-cookie';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { isNil } from 'lodash-es';
import { NEXT_PUBLIC_ENV, NEXT_PUBLIC_HACKLE_CLIENT_KEY } from '../../appConfig';
import useClientAccountProfileQuery from '../queries/useClientAccountProfileQuery';
import useUserStatisticsQuery from '../queries/useUserStatisticsQuery';

const hackleClient = createInstance(NEXT_PUBLIC_HACKLE_CLIENT_KEY || '', {
  devTool: HackleDevTools,
  exposureEventDedupIntervalMillis: 1_000,
  pollingIntervalMillis: 30_000,
  autoOpenDevTool: NEXT_PUBLIC_ENV !== 'production',
});

export function HackleProvider({ children }: { children: ReactNode }) {
  const { data: profile } = useClientAccountProfileQuery();
  const { data: userStat } = useUserStatisticsQuery({
    options: {
      enabled: !!profile,
    },
  });

  const user = {
    id: Cookies.get('deviceId'),
    userId: String(profile?.id),
  };

  useEffect(() => {
    window.hackleClient = hackleClient;
  }, []);

  useEffect(() => {
    const deviceId = Cookies.get('deviceId') ? String(Cookies.get('deviceId')) : undefined;
    deviceId && hackleClient.setDeviceId(deviceId);

    if (!profile) {
      return;
    }

    if (NEXT_PUBLIC_ENV === 'production' && profile.isAdmin) {
      hackleClient.showUserExplorer();
    }

    const userId = profile.id;

    const operations = new HackleSubscriptionOperationsBuilder()
      .marketing(
        profile.notification.isAllowMarketing
          ? HackleSubscriptionStatus.SUBSCRIBED
          : HackleSubscriptionStatus.UNSUBSCRIBED,
      )
      .information(
        profile.notification.isAllowServiceAnnouncement
          ? HackleSubscriptionStatus.SUBSCRIBED
          : HackleSubscriptionStatus.UNSUBSCRIBED,
      )
      .build();

    const isB2c = isNil(profile.group) && isNil(profile.managedGroup);

    userId && hackleClient.setUserId(String(userId));
    hackleClient.setUserProperty('userid', userId);
    hackleClient.setUserProperty('isadmin', profile.isAdmin);
    hackleClient.setUserProperty('isb2b', !isB2c);
    hackleClient.setUserProperty('isb2c', isB2c);
    hackleClient.setUserProperty('isinstructor', profile.isInstructor);
    hackleClient.setUserProperty('username', profile.name);
    hackleClient.setUserProperty('joinedat', profile.joinedAt);
    hackleClient.setPhoneNumber(`${profile.phone?.countryCode ?? ''}${profile.phone?.number}`);

    if (userStat) {
      hackleClient.setUserProperty('paidcoursecount', userStat.userCourseCnt);
    }

    hackleClient.updatePushSubscriptions(operations);
    hackleClient.updateKakaoSubscriptions(operations);
    hackleClient.updateSmsSubscriptions(operations);
  }, [profile, userStat]);

  return (
    <HackleProvider_ hackleClient={hackleClient} user={user} supportSSR>
      {children}
    </HackleProvider_>
  );
}
